// Owner: dashboards-core / dashboards-editor (plan §9.16, §9.32). Keyboard commands of the dashboard
// viewer and builder, discovered by glob (`features/**/commands.ts`).
//
// The builder has two scopes that are active at once while focus is on the canvas: `canvas.grid`
// (canvas-kit: arrows move, ⇧ arrows resize, ↵ configures, Delete, ⌘D, Esc, Tab) and `dashboard.editor`
// (the whole builder: Esc, A or / adds, clipboard, undo). Focus often sits in the palette or the inspector
// instead of the canvas; the builder scope then keeps the verbs that make sense there working and lists
// the canvas-only ones (muted) so the `⌘/` sheet always tells what the builder can do.
import { defineCommand, defineScope } from '@dataroom/interaction';
import { defineAppCommands } from '../../extensions/commands';
import { t } from '../../lib/i18n';

type DashboardViewContext = { edit: () => void };
export const dashboardViewScope = defineScope<DashboardViewContext>({
  id: 'dashboard.view',
  kind: 'route',
  title: () => t('s5.dash.crumb'),
});

export type DashboardEditorContext = {
  /** Leave the builder (asks "Discard N changes?" when the draft has unpublished changes). */
  close: () => void;
  hasSelection: () => boolean;
  /** Clear the selection and put focus back on the canvas, so the next Esc leaves the builder. */
  deselect: () => void;
  add: () => void;
  /** Focus is on the canvas right now (its own `canvas.grid` commands are listed then). */
  canvasFocused: () => boolean;
  canEdit: () => boolean;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  duplicate: () => void;
  copy: () => void;
  cut: () => void;
  paste: () => void;
};
export const dashboardEditorScope = defineScope<DashboardEditorContext>({
  id: 'dashboard.editor',
  kind: 'editor',
  title: () => t('de.editor'),
});

const widgets = () => t('de.group.widgets');
const layout = () => t('de.group.layout');
const never = () => false;
/** Listed (muted) while focus is outside the canvas; on the canvas the `canvas.grid` section lists them. */
const offCanvas = (c: DashboardEditorContext) => !c.canvasFocused();

const clipboardCommand = (name: 'copy' | 'cut' | 'paste', shortcut: string) =>
  defineCommand<DashboardEditorContext>({
    id: `dashboard.editor.${name}`,
    title: () => t(`de.cmd.${name}`),
    ...(name === 'copy' ? { icon: 'copy' as const } : {}),
    // In the editor ⌘C copies widgets (a record peek's "copy link" belongs to another screen).
    ...(name === 'copy' ? { overrides: ['records.peek.copyLink'] } : {}),
    shortcut,
    scope: dashboardEditorScope,
    group: widgets,
    listed: 'always',
    when: (c) => (name === 'paste' ? c.canEdit() : c.hasSelection() && (name === 'copy' || c.canEdit())),
    // Typed on the keyboard, the browser's own copy, cut or paste event follows (the router feeds it).
    run: (c, info) => {
      if (info.source === 'keyboard') return false;
      c[name]();
    },
  });

const arrows = (prefix: string) => ['arrowleft', 'arrowright', 'arrowup', 'arrowdown'].map((a) => prefix + a);

export default defineAppCommands({
  id: 'dashboard.view',
  loadTitles: () => import('./messages'),
  scopes: [dashboardViewScope, dashboardEditorScope],
  commands: [
    // Esc is the one way out: it deselects first, then leaves (asking before it drops unpublished
    // changes), wherever focus is inside the builder or after it was lost (`within-or-idle`).
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.leave',
      scope: dashboardEditorScope,
      title: () => t('de.cmd.escape'),
      shortcut: 'escape',
      group: widgets,
      run: (c) => {
        if (c.hasSelection()) c.deselect();
        else c.close();
      },
    }),
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.add',
      scope: dashboardEditorScope,
      title: () => t('de.addWidget'),
      shortcut: ['a', '/'],
      // In the editor "/" adds a widget (the editor has no search field to focus).
      overrides: ['app.search'],
      group: widgets,
      run: (c) => c.add(),
    }),
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.editor.duplicate',
      scope: dashboardEditorScope,
      title: () => t('de.cmd.duplicate'),
      icon: 'copy',
      shortcut: 'mod+d',
      group: widgets,
      listed: offCanvas,
      when: (c) => c.hasSelection() && c.canEdit(),
      run: (c) => c.duplicate(),
    }),
    clipboardCommand('copy', 'mod+c'),
    clipboardCommand('cut', 'mod+x'),
    clipboardCommand('paste', 'mod+v'),
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.editor.undo',
      scope: dashboardEditorScope,
      title: () => t('de.cmd.undo'),
      shortcut: 'mod+z',
      repeat: true,
      group: widgets,
      listed: offCanvas,
      when: (c) => c.canUndo(),
      run: (c) => c.undo(),
    }),
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.editor.redo',
      scope: dashboardEditorScope,
      title: () => t('de.cmd.redo'),
      shortcut: ['mod+shift+z', { windows: 'ctrl+y' }],
      repeat: true,
      group: widgets,
      listed: offCanvas,
      when: (c) => c.canRedo(),
      run: (c) => c.redo(),
    }),
    // Canvas verbs, listed here while focus is elsewhere in the builder (they work once the canvas has
    // focus: click a widget or Tab to it). `when` is false so the keys stay with the inspector's tabs.
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.editor.move',
      scope: dashboardEditorScope,
      title: () => t('de.cmd.move'),
      shortcut: arrows(''),
      group: layout,
      listed: offCanvas,
      when: never,
      run: never,
    }),
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.editor.resize',
      scope: dashboardEditorScope,
      title: () => t('de.cmd.resize'),
      shortcut: arrows('shift+'),
      group: layout,
      listed: offCanvas,
      when: never,
      run: never,
    }),
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.editor.configure',
      scope: dashboardEditorScope,
      title: () => t('de.cmd.configure'),
      shortcut: 'enter',
      group: layout,
      listed: offCanvas,
      when: never,
      run: never,
    }),
    defineCommand<DashboardEditorContext>({
      id: 'dashboard.editor.delete',
      scope: dashboardEditorScope,
      title: () => t('de.cmd.delete'),
      icon: 'trash',
      shortcut: ['delete', 'backspace'],
      group: layout,
      listed: offCanvas,
      when: never,
      run: never,
    }),
    defineCommand<DashboardViewContext>({
      id: 'dashboard.edit',
      title: () => t('s5.dash.edit'),
      scope: dashboardViewScope,
      shortcut: 'e',
      run: (ctx) => ctx.edit(),
    }),
  ],
});
